import { Component } from '@theme/component';

/**
 * <aromely-diffuser-gallery> — stage gallery (adapted from the fragrance PDP gallery).
 *
 * The scroll-snap track is the source of truth for the active slide: swiping, the
 * arrows, the thumbnails and the keyboard all end up scrolling the track, and the
 * scroll listener repaints the caption pill, the count, the dots, the rail and the
 * arrow states. The active slide's video plays (muted); every other video pauses.
 */
class AromelyDiffuserGallery extends Component {
  #i = 0;
  #lock = false;
  #ac = null;

  connectedCallback() {
    super.connectedCallback();
    const { track } = this.refs;
    if (!track) return;
    this.#ac = new AbortController(); // per connection: the theme editor re-connects elements
    const { signal } = this.#ac;
    track.addEventListener('scroll', () => {
      if (this.#lock) return;
      const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      if (i !== this.#i) { this.#i = i; this.#paint(); }
    }, { passive: true, signal });
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
    }, { signal });
    // Pause everything while the page is hidden; resume the active video on return.
    document.addEventListener('visibilitychange', () => this.#media(), { signal });
    // Start on the colour the purchase area has selected (X25, 25 MAX, TOWER); otherwise show everything.
    const pressed = this.closest('.adm')?.querySelector('.adm__swatch[aria-pressed="true"]');
    if (pressed?.dataset.color) this.filter(pressed.dataset.color);
    else this.#paint();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac?.abort();
  }

  /** Visible slides only — hidden ones (other colours) don't count. */
  get #slides() { return Array.from(this.refs.track?.children ?? []).filter((s) => !s.hidden); }

  /**
   * Show only the slides tagged with this colour (plus untagged ones — videos, shared shots).
   * Called by the purchase area when the shopper picks a colour. @param {string} color
   */
  filter(color) {
    const { track, dots } = this.refs;
    if (!track) return;
    const want = (color || '').toLowerCase().trim();
    const all = Array.from(track.children);
    const tagged = all.some((s) => s.dataset.color);
    for (const slide of all) {
      const c = slide.dataset.color || '';
      slide.hidden = tagged && want !== '' && c !== '' && c !== want;
    }
    for (const button of this.refs.thumbs || []) {
      const c = button.dataset.color || '';
      button.hidden = tagged && want !== '' && c !== '' && c !== want;
    }
    const total = this.#slides.length;
    if (dots) dots.replaceChildren(...Array.from({ length: total }, () => document.createElement('i')));
    this.#i = 0;
    this.#lock = true;
    track.scrollTo({ left: 0, behavior: 'auto' });
    window.setTimeout(() => { this.#lock = false; }, 100);
    this.#paint();
  }

  prev() { this.go(this.#i - 1); }
  next() { this.go(this.#i + 1); }

  /** @param {Event} event */
  thumb(event) {
    const button = event.target.closest('[data-i]');
    if (!button) return;
    // data-i is the slide's original position; map it to its position among the visible slides.
    const k = this.#slides.findIndex((s) => s.dataset.i === button.dataset.i);
    if (k >= 0) this.go(k);
  }

  /** @param {number} i */
  go(i) {
    const { track } = this.refs;
    if (!track) return;
    const max = this.#slides.length - 1;
    this.#i = Math.max(0, Math.min(max, i));
    this.#lock = true;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: this.#i * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    window.setTimeout(() => { this.#lock = false; }, 450);
    this.#paint();
  }

  #paint() {
    const slides = this.#slides;
    const total = slides.length;
    const active = slides[this.#i];
    const { tag, count, dots, prev, next } = this.refs;
    const cap = active?.dataset.cap || '';
    if (tag) { tag.textContent = cap; tag.hidden = !cap; }
    if (count) count.textContent = `${this.#i + 1} / ${total}`;
    for (const [k, slide] of slides.entries()) slide.setAttribute('aria-label', `${k + 1} of ${total}`);
    if (dots) for (const [k, dot] of Array.from(dots.children).entries()) dot.className = k === this.#i ? 'on' : '';
    const thumbs = this.refs.thumbs || [];
    const activeI = active?.dataset.i;
    for (const button of thumbs) {
      const on = button.dataset.i === activeI;
      button.setAttribute('aria-current', String(on));
      if (on) this.#reveal(button);
    }
    if (prev) prev.disabled = this.#i === 0;
    if (next) next.disabled = this.#i >= total - 1;
    this.#media();
  }

  /** Scroll the rail (never the page) so the active thumbnail is in view. @param {HTMLElement} button */
  #reveal(button) {
    const { rail } = this.refs;
    if (!rail) return;
    const r = rail.getBoundingClientRect();
    const b = button.getBoundingClientRect();
    if (b.left < r.left) rail.scrollLeft -= r.left - b.left + 8;
    else if (b.right > r.right) rail.scrollLeft += b.right - r.right + 8;
    if (b.top < r.top) rail.scrollTop -= r.top - b.top + 8;
    else if (b.bottom > r.bottom) rail.scrollTop += b.bottom - r.bottom + 8;
  }

  /** Shopper's choice wins; with prefers-reduced-motion nothing starts on its own. */
  #paused = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  togglePlay() {
    this.#paused = !this.#paused;
    this.#media();
  }

  #media() {
    const hidden = document.visibilityState === 'hidden';
    let activeVideo = null;
    for (const [k, slide] of this.#slides.entries()) {
      const video = slide.querySelector('video');
      if (!video) continue;
      if (k === this.#i && !hidden && !this.#paused) {
        if (video.preload === 'none') video.preload = 'metadata';
        video.play().catch(() => { this.#paused = true; this.#syncPlayPause(video); });
        activeVideo = video;
      } else {
        video.pause();
        if (k === this.#i) activeVideo = video;
      }
    }
    this.#syncPlayPause(activeVideo);
  }

  /** The pause/play control shows only while a video slide is active. @param {HTMLVideoElement|null} video */
  #syncPlayPause(video) {
    const btn = this.refs.playPause;
    if (!btn) return;
    btn.hidden = !video;
    if (!video) return;
    btn.setAttribute('aria-pressed', String(this.#paused));
    btn.setAttribute('aria-label', this.#paused ? 'Play video' : 'Pause video');
  }
}

if (!customElements.get('aromely-diffuser-gallery')) {
  customElements.define('aromely-diffuser-gallery', AromelyDiffuserGallery);
}
